import { camelCase, memoize } from 'lodash'
import { Dispatch } from 'redux'
import { createSelector } from 'reselect'

import API from '../api/api'
import { getAssetLink } from '../helpers/get-asset-link'
import {
  Building,
  BuildingFilterItem,
  BuildingSelectiorActions,
  BuildingSelectorState,
  SetActiveBuildingAction,
  SetBuildingsAction,
} from './types'
import { LOGOUT_USER } from './userDataReducer'

/** --- ACTIONS --- */
export const SET_BUILDINGS = 'SET_BUILDINGS' as const
export const SET_ACTIVE_BUILDING = 'SET_ACTIVE_BUILDING' as const
export const UPDATE_BUILDING = 'UPDATE_BUILDING' as const
export const CREATE_BUILDING = 'CREATE_BUILDING' as const
export const UPDATE_BUILDING_APARTMENTS_ID = 'UPDATE_BUILDING_APARTMENTS_ID' as const

/** --- INITIAL --- */
const initialState: BuildingSelectorState = {
  activeBuilding: null,
  buildings: [],
  buildingsLoaded: false,
}

/** --- REDUCER --- */
const buildingSelectorReducer = (
  state: BuildingSelectorState = initialState,
  action: BuildingSelectiorActions,
): BuildingSelectorState => {
  switch (action.type) {
    case SET_BUILDINGS: {
      const formatted = formatBuildingsForFilter(action.buildings)
      const defaultBuilding = action.defaultBuildingId
        ? formatted.find((building) => building.id === action.defaultBuildingId)
        : null
      return {
        ...state,
        activeBuilding: state.activeBuilding || defaultBuilding || formatted[0] || null,
        buildings: action.buildings,
        buildingsLoaded: true,
      }
    }
    case SET_ACTIVE_BUILDING:
      return { ...state, activeBuilding: action.building }

    case UPDATE_BUILDING: {
      const updatedList = state.buildings.map((b) =>
        b.id === action.buildingData.id ? action.buildingData : b,
      )
      const newActive =
        state.activeBuilding && state.activeBuilding.id === action.buildingData.id
          ? (formatBuildingsForFilter([action.buildingData])[0] ?? state.activeBuilding)
          : state.activeBuilding
      return { ...state, activeBuilding: newActive, buildings: updatedList }
    }

    case UPDATE_BUILDING_APARTMENTS_ID: {
      const updatedList = state.buildings.map((b) =>
        b.id === action.buildingData.id ? action.buildingData : b,
      )
      return { ...state, buildings: updatedList }
    }

    case CREATE_BUILDING:
      return { ...state, buildings: [action.buildingData, ...state.buildings] }

    case LOGOUT_USER:
      return initialState

    default:
      return state
  }
}

export default buildingSelectorReducer

/** --- ACTION CREATORS --- */

type AppState = { buildingSelector: BuildingSelectorState }
type AppGetState = () => AppState

export const setBuildings = (
  buildings: Building[],
  defaultBuildingId?: number,
): SetBuildingsAction => ({
  buildings,
  defaultBuildingId,
  type: SET_BUILDINGS,
})

export const setActiveBuilding = (
  building: BuildingFilterItem | null,
): SetActiveBuildingAction => ({
  building,
  type: SET_ACTIVE_BUILDING,
})

export const fetchBuildings = (defaultBuildingId?: number) => (dispatch: Dispatch<AppState>) =>
  API.getAllBuildings()
    .then((res: Building[]) => {
      dispatch(setBuildings(res, defaultBuildingId))
      return Promise.resolve(res)
    })
    .catch((err: unknown) => Promise.reject(err))

export const fetchSingleBuilding = (buildingId: number) => (dispatch: Dispatch<AppState>) =>
  API.getSingleBuilding(buildingId)
    .then((res: Building) => {
      dispatch({ buildingData: res, type: UPDATE_BUILDING })
      return Promise.resolve(res)
    })
    .catch((err: unknown) => Promise.reject(err))

export const createBuilding = (data: Partial<Building>) => (dispatch: Dispatch<AppState>) =>
  API.createNewBuilding(data)
    .then((res: Building) => {
      dispatch({ buildingData: res, type: CREATE_BUILDING })
      return Promise.resolve(res)
    })
    .catch((err: unknown) => Promise.reject(err))

export const updateBuilding =
  (data: Partial<Building>, id: number) => (dispatch: Dispatch<AppState>) =>
    API.updateBuildingData(data, id)
      .then((res: Building) => {
        dispatch({ buildingData: res, type: UPDATE_BUILDING })
        return Promise.resolve(res)
      })
      .catch((err: unknown) => Promise.reject(err))

export const updateBuildingApartmentsId =
  (buildingId: number, apartmentsId: number) => (dispatch: Dispatch<AppState>) =>
    API.updateBuildingApartmentsId(buildingId, apartmentsId)
      .then((res: { data: Building }) => {
        dispatch({ buildingData: res.data, type: UPDATE_BUILDING_APARTMENTS_ID })
        return Promise.resolve(res.data)
      })
      .catch((err: unknown) => Promise.reject(err))

export const addLuxer =
  (buildingId: number, luxerId: number) =>
  async (dispatch: Dispatch<AppState>, getState: AppGetState) => {
    try {
      const { data } = await API.addLuxerToBuilding({ building: buildingId, luxerOneId: luxerId })
      const {
        buildingSelector: { buildings },
      } = getState()
      const building: Building | undefined = buildings.find((b: Building) => b.id === buildingId)
      if (!building) return

      const buildingData: Building = {
        ...building,
        luxerOneLocations: [...(building.luxerOneLocations ?? []), data],
      }
      dispatch({ buildingData, type: UPDATE_BUILDING })
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error))
    }
  }

export const removeLuxer =
  (buildingId: number, luxerId: number) =>
  async (dispatch: Dispatch<AppState>, getState: AppGetState) => {
    try {
      await API.deleteLuxerFromBuilding(luxerId)
      const {
        buildingSelector: { buildings },
      } = getState()
      const building: Building | undefined = buildings.find((b: Building) => b.id === buildingId)
      if (!building) return

      const buildingData: Building = {
        ...building,
        luxerOneLocations: (building.luxerOneLocations ?? []).filter((l) => l.id !== luxerId),
      }
      dispatch({ buildingData, type: UPDATE_BUILDING })
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error))
    }
  }

export const updateBuildingIntegration =
  (data: Partial<Building>, id: number, integration: string) =>
  (dispatch: Dispatch<AppState>, getState: AppGetState) => {
    if (integration === 'brivo') {
      return API.updateBuildingIntegrationBrivo(data)
    } else if (integration === 'salto-space') {
      return API.updateBuildingIntegrationSaltoSpace(data)
    } else if (integration === 'swiftlane') {
      return API.updateBuildingIntegrationSwiftlane(data)
    } else {
      API.updateBuildingIntegration(data, id)
        .then(() => {
          const { buildingSelector } = getState()
          const building: Building | undefined = buildingSelector.buildings.find(
            (b: Building) => b.id === id,
          )
          if (building) {
            const updatedBuildingData: Building = { ...building, ...data }
            dispatch({ buildingData: updatedBuildingData, type: UPDATE_BUILDING })
          }
          return Promise.resolve()
        })
        .catch((err: unknown) => Promise.reject(err))
    }
  }

export const disconnectBuildingIntegration =
  (buildingId: number, pms: string) =>
  async (dispatch: Dispatch<AppState>): Promise<void> => {
    await API.disconnectBuildingIntegration(buildingId, camelCase(pms))
    const refreshed: Building = await API.getSingleBuilding(buildingId)
    dispatch({ buildingData: refreshed, type: UPDATE_BUILDING })
  }

/** --- HELPERS --- */

export const formatBuildingsForFilter = (buildings: Building[]): BuildingFilterItem[] => {
  if (buildings && buildings.length > 0) {
    return buildings.map((item) => ({
      ...item,
      avatar:
        getAssetLink({ file: item.logo ?? undefined, path: 'chitown-building' }) ||
        getAssetLink({ file: item.avatar ?? undefined, path: 'chitown-building' }) ||
        'city',
      code: item.code,
      communicateSMSAvailable: item.communicateSMSAvailable,
      entrataConfig: !!(item.entrataManagementId && item.entrataPropertyId),
      id: item.id,
      realPageConfig: !!(item.realPageManagementId && item.realPagePropertyId),
      rentManagerConfig: !!(item.rentManagerManagementId && item.rentManagerPropertyId),
      resmanConfig: !!(item.resmanManagementId && item.resmanPropertyId),
      saltoSpaceConfig: !!item.saltoSpaceAvailable,
      tags: item.tags,
      text: item.name || item.text || '',
      timezone: item.timezone,
      yardiConfig: !!(item.yardiManagementId && item.yardiPropertyId),
    }))
  }
  return []
}

/** --- SELECTORS --- */

export const selectBuildings = (state: AppState): Building[] => state.buildingSelector.buildings
export const selectActiveBuilding = (state: AppState): BuildingFilterItem | null =>
  state.buildingSelector.activeBuilding
export const selectBuildingsLoaded = (state: AppState): boolean =>
  state.buildingSelector.buildingsLoaded

export const selectBuildingById = createSelector(selectBuildings, (buildings: Building[]) =>
  memoize((id: string | number) => {
    if (id === 'new') {
      return { building: {} }
    }
    const numId = typeof id === 'string' ? parseInt(id, 10) : id
    return buildings.find((b) => b.id === numId) || buildings[0] || { building: {} }
  }),
)

// Note: unlike selectBuildingById there is no fallback to the first building for an unknown id
export const selectBuildingStrict = createSelector(selectBuildings, (buildings: Building[]) =>
  memoize((id: string | number | undefined): Building | null => {
    const numId = Number(id)
    if (id === undefined || id === '' || !Number.isInteger(numId)) return null
    return buildings.find((building) => building.id === numId) ?? null
  }),
)

export const selectBuildingsForFilter = createSelector(selectBuildings, (buildings: Building[]) =>
  formatBuildingsForFilter(buildings),
)
